import { useEffect, useRef, useState } from 'react';

import { calculateTimeToSuccessionOMSOpen, formatSuccessionTime, getMillisecondsToSyncWithClock } from '../DateTimeHelpers';

/**
 * useSuccessionTimer
 *
 * This custom React hook is designed to manage a countdown timer for an FX exchange service.
 * The timer counts down to the next opening time.
 */
export const useSuccessionTimer = () => {
  const [ timerValue, setTimerValue ] = useState<number | null>(null);
  const [ isEnabled, setIsEnabled ] = useState<boolean>(true);
  const timerId = useRef<ReturnType<typeof setInterval> | null>(null);

  const calculateValues = () => {
    const { nextMarketOpen } = calculateTimeToSuccessionOMSOpen();

    if (nextMarketOpen <= 0) {
      setIsEnabled(false);
      setTimerValue(0);
    } else {
      setIsEnabled(true);
      setTimerValue(Math.ceil(nextMarketOpen));
    }
  };

  // Function to update the timer every second
  const timerTick = () => {
    calculateValues();

    if (timerValue !== null && timerValue <= 0) {
      setIsEnabled(false);
      if (timerId.current) {
        clearInterval(timerId.current);
        timerId.current = null;
      }
    }
  };

  useEffect(() => {
    const msLeft = getMillisecondsToSyncWithClock();
    const timeoutId = setTimeout(() => {
      timerId.current = setInterval(timerTick, 1000);
    }, msLeft);

    calculateValues();

    return () => {
      clearTimeout(timeoutId);
      if (timerId.current) {
        clearInterval(timerId.current);
      }
    };
  }, []); // eslint-disable-line

  const timerValueAsString = timerValue !== null ? formatSuccessionTime(timerValue) : '00:00:00';

  return { timerValueAsString, isEnabled };
};


